import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { X, Smartphone, Share, PlusSquare, Copy, Check } from "lucide-react";

export function DownloadModal({ open, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = window.location.origin;

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {}); // clipboard permission denied/unavailable - QR code + manual read-off still work
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
          data-testid="download-qr-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 12 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-panel rounded-2xl p-7 max-w-sm w-full relative border border-cyan-400/20"
          >
            <button
              data-testid="download-modal-close-button"
              onClick={onClose}
              className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 mb-1">
              <Smartphone className="w-4 h-4 text-cyan-300" />
              <span className="text-[10px] font-mono-data uppercase tracking-[0.2em] text-cyan-400/80">Take AVA With You</span>
            </div>
            <h2 className="font-display font-semibold text-white text-lg mb-4">Scan to open on your phone</h2>

            <div className="bg-white rounded-xl p-4 w-fit mx-auto mb-5" data-testid="download-qr-code">
              <QRCodeSVG value={url} size={180} bgColor="#ffffff" fgColor="#030509" level="M" />
            </div>

            <div className="space-y-3 text-xs text-slate-400 leading-relaxed">
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-cyan-400/15 text-cyan-300 flex items-center justify-center text-[10px] font-mono-data flex-shrink-0 mt-0.5">1</span>
                <span>Point your phone's camera at the code above - it opens AVA right in your browser.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-cyan-400/15 text-cyan-300 flex items-center justify-center text-[10px] font-mono-data flex-shrink-0 mt-0.5">2</span>
                <span>
                  <strong className="text-slate-300">iPhone/iPad:</strong> tap <Share className="w-3 h-3 inline -mt-0.5" />{" "}
                  Share, then <PlusSquare className="w-3 h-3 inline -mt-0.5" /> "Add to Home Screen".
                  <br />
                  <strong className="text-slate-300">Android:</strong> open the browser menu and tap "Install app".
                </span>
              </div>
            </div>

            <button
              data-testid="download-copy-link-button"
              onClick={handleCopy}
              className="mt-5 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-white/5 border border-white/15 text-slate-300 hover:bg-white/10 text-[10px] font-mono-data uppercase tracking-wider transition-colors"
            >
              {copied ? (
                <><Check className="w-3.5 h-3.5 text-emerald-400" /> Link Copied</>
              ) : (
                <><Copy className="w-3.5 h-3.5" /> Copy Link Instead</>
              )}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
